import {ref} from "vue";
import axios from "axios";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import {errorToast, successToast} from "@/composables/useToasts";

const modalApiChangeLog = ref(null);
const modalTitle = ref('');
const modalDescription = ref('');
const modalBreakingChange = ref(false);
const showModal = ref(false);
const showDeleteWarningModal = ref(false);

export const useApiChangeLogs = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['api-change-logs'],
    queryFn: async () => {
      const response = await axios.get(`/ajax/v2/admin/api-change-logs`);

      return response.data.data;
    },
  });

  const openEditModal = (apiChangeLog) => {
    modalApiChangeLog.value = apiChangeLog;
    modalTitle.value = apiChangeLog.title;
    modalDescription.value = apiChangeLog.description;
    modalBreakingChange.value = apiChangeLog.breaking_change;

    showModal.value = true;
  };

  const closeEditModal = () => {
    modalApiChangeLog.value = null;
    modalTitle.value = '';
    modalDescription.value = '';
    modalBreakingChange.value = false;

    showModal.value = false;
  };

  const openDeleteWarningModal = (apiChangeLog) => {
    modalApiChangeLog.value = apiChangeLog;
    showDeleteWarningModal.value = true;
  };

  const closeDeleteWarningModal = () => {
    modalApiChangeLog.value = null;
    showDeleteWarningModal.value = false;
  };

  const editApiChangeLog = () => useMutation({
    mutationFn: (approve = false) => axios.post(
      `/ajax/v2/admin/api-change-logs/${modalApiChangeLog.value.id}`,
      {
        approve: approve,
        title: modalTitle.value,
        description: modalDescription.value,
        breaking_change: modalBreakingChange.value
      }
    ),
    onError: (error) => {
      console.error(error);
      errorToast('Something went wrong');
    },
    onSuccess: (_, variables) => {
      successToast(`Submitted ${variables.approve ? 'and approved ' : ''} successfully.`);
      closeEditModal();
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['api-change-logs']});
    },
  });

  const deleteApiChangeLog = () => useMutation({
    mutationFn: () => axios.delete(`/ajax/v2/admin/api-change-logs/${apiChangeLog.value.id}`),
    onError: (error) => {
      console.error(error);
      errorToast('Something went wrong');
    },
    onSuccess: (_, variables) => {
      successToast(`Deleted successfully.`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: ['api-change-logs']});
    },
  });

  return {
    ...query,
    modalTitle,
    modalDescription,
    modalBreakingChange,
    showModal,
    showDeleteWarningModal,
    openEditModal,
    closeEditModal,
    openDeleteWarningModal,
    closeDeleteWarningModal,
    editApiChangeLog,
    deleteApiChangeLog,
  };
};
